/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useRef, useState } from 'react';
import root from 'window-or-global';
import { useLabels } from '@/hooks/useLabels';
import { fetchOrderDetails } from '@/actions/details';
import type {
  ConstraintSelectionHash,
  Deal,
  DealConstraint,
  DealConstraintSkuDetails,
  SkuDetails,
} from '../../../interface';
import { useDealConstraintSkuDetails } from '../../../api/getDealConstraintSkuDetails';
import { useDeal } from '../../../api/getDeal';
import { BundleDealDetails } from './Details';
import { BundleDealConstraintPicker } from './ConstraintPicker';
import { clickTrack } from '@/utils/analytics/clickTrack';
import type { CustomLinkName } from '@/types/analytics';
import { sessionStorage } from '@/utils/sessionStorage';
import { isPdpPage } from '@/utils/analytics/isPdpPage';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useAvailabilityInfoData } from '@/features/orders/api/getAvailabilityInfo';
import { usePostDealToCart } from '../../../api/postDealToCart';
import { getErrorCodeDescription } from '@/utils/errorsHandling';
import { useQueryClient } from '@tanstack/react-query';
import { useExecuteOnce } from '@/hooks/useExecuteOnce';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const formatToOriginalPartTypeIdList = (originalPartType = '0') => {
  const originalPartInt = Number(originalPartType);
  const charArray = [...originalPartType];
  return `${originalPartInt}-${charArray[0] === '0' ? '0' : '10'}`;
};

const trackAddToCartDealBundle = (
  dealInfo: {
    skuId: string;
    dealId: string;
    dealName: string | null;
    dealDescription: string | null;
  },
  itemsToAdd: Array<{
    skuId: string;
    constraintId: string;
    productId: string;
    originalPartType: string;
    vehicleId: string | undefined;
    storeNumber: string | undefined;
    fulfillmentTypeId: string | number | null | undefined;
    quantity: number;
    price: number;
    brandName: string;
    partGroupId: string | null;
    partTerminologyId: string | null;
  }>,
  analyticsPageType: string,
  isAcesEnabled: boolean,
) => {
  let productFulfillmentTypeList: string[] = [];
  let productIdList: string[] = [];
  let originalPartTypeIdList: string[] = [];
  let productRepositoryIdList: string[] = [];
  let productBrandList: string[] = [];
  let productPriceList: string[] = [];
  let productQuantityList: string[] = [];
  let productRequestedQuantityList: string[] = [];
  let partGroupIdArray: string[] = [];
  let partTerminologyIdArray: string[] = [];

  itemsToAdd.forEach((item) => {
    if (item.fulfillmentTypeId) {
      productFulfillmentTypeList = [
        ...productFulfillmentTypeList,
        item.fulfillmentTypeId.toString(),
      ];
    }
    productIdList = [...productIdList, item.skuId];

    productBrandList = [...productBrandList, item.brandName];
    productPriceList = [...productPriceList, new Intl.NumberFormat('en-US').format(item.price)];
    productQuantityList = [...productQuantityList, item.quantity.toString()];
    productRequestedQuantityList = [...productRequestedQuantityList, item.quantity.toString()];

    if (isAcesEnabled) {
      partGroupIdArray = [...partGroupIdArray, item.partGroupId || ''];
      partTerminologyIdArray = [...partTerminologyIdArray, item.partTerminologyId || ''];
    } else {
      originalPartTypeIdList = [
        ...originalPartTypeIdList,
        formatToOriginalPartTypeIdList(item.originalPartType),
      ];
      productRepositoryIdList = [...productRepositoryIdList, item.productId];
    }
  });
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  const cartAddToCartDealBundle: CustomLinkName = 'Add to Cart - Deal Bundle';
  const data = {
    eventType: 'addToCart',
    productFulfillmentTypeList,
    productIdList,
    dealId: dealInfo.dealId,
    dealName: dealInfo.dealName,
    dealDescription: dealInfo.dealDescription,
    productBrandList,
    productPriceList,
    productRecommendationTypeList: 'Deal bundle',
    productFindingMethod: 'Finder',
    finderFeature: 'Deal Drawer',
    productQuantityList,
    productRequestedQuantityList,
    pageName,
    pageType,
    siteSection,
    customLinkName: `${analyticsPageType || pageType}: ${cartAddToCartDealBundle}`,
    ...(isAcesEnabled
      ? {
          partGroupId: partGroupIdArray,
          partTerminologyId: partTerminologyIdArray,
        }
      : {
          originalPartTypeIdList,
          productRepositoryIdList,
        }),
    ...(isPdpPage(pageType) && {
      enhancedPDPContent: sessionStorage.getItem('enhancedPDPContent') === 'true',
    }),
  };
  clickTrack(data);
};

type ConstraintForSwap = {
  constraint: DealConstraint;
  constraintSkuDetails: DealConstraintSkuDetails;
  skipInitialSelection?: boolean;
  allowDeselection?: boolean;
};

const labelMap = {
  label_deal_bundle_unexpected_error: 'label_deal_bundle_unexpected_error',
};

type Props = {
  analyticsPageType: string;
  parentSkuDetails: SkuDetails;
  skuFulfillmentTypeId: number | string | null | undefined;
  skuIsVehicleSpecific: boolean | undefined;
  deal: Deal;
  onAddedToCart: () => void;
  onBackClick: (() => void) | undefined;
  onClose: () => void;
  trackOnDealSelection: (dealInfo: {
    dealId: string;
    name: string | null;
    description: string | null;
  }) => void;
  handleErrorAnalytics: (a: string, b: string) => void;
  dealItemSkuSet: Set<string>;
  setminiCartDealData: (value: React.SetStateAction<Set<string>>) => void;
};

export const BundleDealV2 = ({
  analyticsPageType,
  parentSkuDetails,
  skuFulfillmentTypeId,
  skuIsVehicleSpecific,
  deal,
  onAddedToCart,
  onBackClick,
  onClose,
  trackOnDealSelection,
  handleErrorAnalytics,
  dealItemSkuSet,
  setminiCartDealData,
}: Props) => {
  const queryClient = useQueryClient();
  const labels = useLabels(labelMap);
  const { id: skuId } = parentSkuDetails;
  const { id: dealId } = deal;

  const dispatch = useDispatch();
  const [addToCartError, setAddToCartError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isFirst = useRef(true);
  const [constraintSelections, setConstraintSelections] = useState<ConstraintSelectionHash | null>(
    null,
  );
  const [constraintForSwap, setConstraintForSwap] = useState<ConstraintForSwap | null>(null);
  const [isSwapItemClicked, setIsSwapItemClicked] = useState(false);
  const autoOpenedConstraint = useRef<{
    constraint: DealConstraint;
    constraintSkuDetails: DealConstraintSkuDetails;
  } | null>(null);
  const [autoOpenedConstraintId, setAutoOpenedConstraintId] = useState<string | undefined>(
    undefined,
  );
  const { data: headerData, refetch: refetchHeaderData } = useHeaderData();
  const { refetch: refetchAvailabilityInfo } = useAvailabilityInfoData();
  const storeNumber = headerData?.storeNumber;
  const preferredVehicleId = usePreferredVehicle()?.catalogVehicleId;

  const { data: dealDetailsData, isError: isDealError } = useDeal({
    dealId,
    skuId,
    fulfillmentOption: String(skuFulfillmentTypeId),
    onError: (err) =>
      handleErrorAnalytics(err.response?.statusText ?? '', err.response?.status.toString() ?? ''),
  });

  const { mutateAsync: postDealAddToCart } = usePostDealToCart();

  let selectedSkus: string[] = [];

  if (isFirst.current) {
    if (dealDetailsData?.constraints) {
      let parentSkuFound = false;

      selectedSkus = Object.values(dealDetailsData.constraints).map((constraintObj) => {
        const constraint: DealConstraint = constraintObj;

        if (parentSkuFound) {
          return constraint.skus[0];
        } else {
          parentSkuFound = !!constraint.skus.find((s) => s === skuId);

          if (parentSkuFound) {
            return skuId;
          } else {
            return constraint.skus[0];
          }
        }
      });
    }
  } else {
    if (constraintSelections) {
      selectedSkus = Object.values(constraintSelections).map((constraintSkuDetailsObj) => {
        const constraintSkuDetails: DealConstraintSkuDetails = constraintSkuDetailsObj;
        return constraintSkuDetails.id;
      });
    }
  }

  const { data: dealConstraintSkuDetails, isError: isDealConstraintError } =
    useDealConstraintSkuDetails({
      dealId,
      parentSku: skuId,
      fulfillmentOption: String(skuFulfillmentTypeId),
      constraintSkusList: selectedSkus,
      onError: (err) =>
        handleErrorAnalytics(err.response?.statusText ?? '', err.response?.status.toString() ?? ''),
    });

  if (isFirst.current && dealConstraintSkuDetails && dealDetailsData) {
    const skuArray: DealConstraintSkuDetails[] = Object.values(dealConstraintSkuDetails.skus);
    const initialConstraintSelections: Record<string, DealConstraintSkuDetails> = {};
    const constraints = Object.values(dealDetailsData.constraints);

    constraints.forEach((constraint, index) => {
      const defaultSkuId = selectedSkus[index];
      const sku = skuArray.find((sku) => sku.id === defaultSkuId);
      if (sku) {
        initialConstraintSelections[constraint.id] = sku;
      }
    });
    isFirst.current = false;
    setConstraintSelections(initialConstraintSelections);

    // Find brake pads and rotors constraints using text matching
    let brakePadsConstraint: DealConstraint | null = null;
    let brakePadsConstraintSku: DealConstraintSkuDetails | null = null;
    let rotorsConstraint: DealConstraint | null = null;
    let rotorsConstraintSku: DealConstraintSkuDetails | null = null;

    for (const constraint of constraints) {
      const constraintSkuIds = constraint.skus;

      const hasBrakePads = constraintSkuIds.some((skuId) => {
        const sku = skuArray.find((s) => s.id === skuId);
        const descriptionLower = (sku?.description || '').toLowerCase();
        return (
          descriptionLower.includes('brake pad') ||
          descriptionLower.includes('brake pads') ||
          descriptionLower.includes('balatas')
        );
      });

      // Check if this constraint contains rotors using text matching
      const hasRotors = constraintSkuIds.some((skuId) => {
        const sku = skuArray.find((s) => s.id === skuId);
        const descriptionLower = (sku?.description || '').toLowerCase();
        return descriptionLower.includes('rotor') || descriptionLower.includes('rotors');
      });

      if (hasBrakePads && !brakePadsConstraint) {
        brakePadsConstraint = constraint;
        brakePadsConstraintSku = initialConstraintSelections[constraint.id];
      }

      if (hasRotors && !rotorsConstraint) {
        rotorsConstraint = constraint;
        rotorsConstraintSku = initialConstraintSelections[constraint.id];
      }
    }

    // Open the OPPOSITE constraint (secondary item, not the parent item)
    const parentDescriptionLower = (parentSkuDetails.description || '').toLowerCase();
    const isBrakePadsDeal =
      parentDescriptionLower.includes('brake pad') ||
      parentDescriptionLower.includes('brake pads') ||
      parentDescriptionLower.includes('balatas');
    const isRotorsDeal =
      parentDescriptionLower.includes('rotor') || parentDescriptionLower.includes('rotors');

    let constraintToOpen: DealConstraint | null = null;
    let constraintSkuToOpen: DealConstraintSkuDetails | null = null;

    // If parent is brake pads, open rotors (secondary). If parent is rotors, open brake pads (secondary)
    if (isBrakePadsDeal && rotorsConstraint && rotorsConstraintSku) {
      constraintToOpen = rotorsConstraint;
      constraintSkuToOpen = rotorsConstraintSku;
    } else if (isRotorsDeal && brakePadsConstraint && brakePadsConstraintSku) {
      constraintToOpen = brakePadsConstraint;
      constraintSkuToOpen = brakePadsConstraintSku;
    }

    if (constraintToOpen && constraintSkuToOpen && constraintToOpen.skus.length > 1) {
      autoOpenedConstraint.current = {
        constraint: constraintToOpen,
        constraintSkuDetails: constraintSkuToOpen,
      };
      setAutoOpenedConstraintId(constraintToOpen.id);
      setConstraintForSwap({
        constraint: constraintToOpen,
        constraintSkuDetails: constraintSkuToOpen,
        skipInitialSelection: true,
        allowDeselection: true,
      });
    }
  }

  useEffect(() => {
    if (constraintSelections && dealItemSkuSet.size < Object.entries(constraintSelections).length) {
      const newSet = new Set(dealItemSkuSet);
      Object.entries(constraintSelections).forEach((x) => {
        newSet.add(x[1].id);
      });
      setminiCartDealData(newSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constraintSelections, setminiCartDealData]);

  const { acesEnabled: isAcesEnabled } = useAcesFlags();

  useExecuteOnce({
    enabled: Boolean(deal.id && deal.name),
    fn: () => {
      trackOnDealSelection({
        dealId: deal.id,
        name: deal.name,
        description: deal.description,
      });
    },
  });

  const addDealToCart = async () => {
    try {
      if (isSaving || !dealDetailsData || !constraintSelections) {
        return;
      }

      setAddToCartError(null);
      const { constraints } = dealDetailsData;
      const dealInfo = {
        skuId,
        dealId,
        dealName: deal.name,
        dealDescription: deal.description,
      };
      const itemsToAdd = Object.entries(constraintSelections).map(
        ([constraintId, constraintSelectionObj]) => {
          const constraintSelection: DealConstraintSkuDetails = constraintSelectionObj;
          const { minQty } = constraints[constraintId];
          const {
            id: skuId,
            productId,
            productBrandName: brandName,
            productTotalPrice: price,
            originalPartType,
            partGroupId,
            partTerminologyId,
          } = constraintSelection;

          return {
            skuId,
            constraintId,
            productId,
            originalPartType,
            vehicleId: preferredVehicleId,
            storeNumber,
            fulfillmentTypeId: skuFulfillmentTypeId,
            quantity: minQty,
            price,
            brandName,
            partGroupId,
            partTerminologyId,
          };
        },
      );
      setIsSaving(true);

      await postDealAddToCart({ skuId, dealId, selectedConstraints: itemsToAdd });

      await Promise.all([
        dispatch(fetchOrderDetails(undefined, queryClient)),
        refetchHeaderData(),
        refetchAvailabilityInfo(),
      ]);
      onAddedToCart();
      trackAddToCartDealBundle(dealInfo, itemsToAdd, analyticsPageType, isAcesEnabled);
    } catch (e) {
      setAddToCartError(getErrorCodeDescription(e) || labels.label_deal_bundle_unexpected_error);
    } finally {
      setIsSaving(false);
    }
  };

  const swapClickHandler = (
    constraint: DealConstraint,
    constraintSkuDetails: DealConstraintSkuDetails,
  ) => {
    setIsSwapItemClicked(true);
    // Ensure we get the latest selection from constraintSelections
    const latestSelection = constraintSelections?.[constraint.id] || constraintSkuDetails;
    setConstraintForSwap({
      constraint,
      constraintSkuDetails: latestSelection,
    });
  };

  const updateSelectedItem = (constraintSkuDetails: DealConstraintSkuDetails) => {
    if (!constraintForSwap) {
      return;
    }

    const { constraint } = constraintForSwap;

    // Clear auto-opened constraint ID after user makes a selection from the auto-opened picker
    if (constraint.id === autoOpenedConstraintId && constraintForSwap.skipInitialSelection) {
      setAutoOpenedConstraintId(undefined);
    }

    setConstraintForSwap(null);
    setIsSwapItemClicked(false);
    isFirst.current = false;
    setminiCartDealData(new Set());
    setConstraintSelections((prevSelections) => ({
      ...(prevSelections || {}),
      [constraint.id]: constraintSkuDetails,
    }));
  };

  const onConstraintPickerBack = () => {
    // If swap item was clicked, go back to deals overview (BundleDealDetails)
    if (isSwapItemClicked) {
      setConstraintForSwap(null);
      setIsSwapItemClicked(false);
    } else {
      // For auto-opened constraint (not a swap), go back to BundleDealPickerOptionList
      if (onBackClick) {
        autoOpenedConstraint.current = null;
        setAutoOpenedConstraintId(undefined);
        onBackClick();
      }
    }
  };

  const onDealDetailsBack = () => {
    if (autoOpenedConstraint.current && constraintSelections) {
      const { constraint } = autoOpenedConstraint.current;
      const currentSelection = constraintSelections[constraint.id];

      setConstraintForSwap({
        constraint: constraint,
        constraintSkuDetails: currentSelection,
        allowDeselection: true,
      });
    } else {
      if (onBackClick) {
        onBackClick();
      }
    }
  };

  useEffect(() => {
    if (!constraintForSwap) {
      return;
    }

    const newContentHeader = document.querySelector<HTMLElement>('#constraint-picker-title');

    if (newContentHeader) {
      newContentHeader.setAttribute('tabindex', '-1');
      newContentHeader.focus();
    }
  }, [constraintForSwap]);

  if (constraintForSwap) {
    // Check if this is the auto-opened brake pad or rotor constraint
    const isAutoOpenedBrakePadOrRotor =
      constraintForSwap.constraint.id === autoOpenedConstraintId &&
      constraintForSwap.skipInitialSelection === true;

    return (
      <BundleDealConstraintPicker
        parentSkuDetails={parentSkuDetails}
        deal={deal}
        constraint={constraintForSwap.constraint}
        initialSelection={constraintForSwap.constraintSkuDetails}
        skipInitialSelection={constraintForSwap.skipInitialSelection}
        allowDeselection={constraintForSwap.allowDeselection}
        onUpdate={updateSelectedItem}
        onBackClick={onConstraintPickerBack}
        onClose={onClose}
        fulfillmentOption={String(skuFulfillmentTypeId)}
        isAutoOpenedBrakePadOrRotor={isAutoOpenedBrakePadOrRotor}
      />
    );
  }

  return (
    <BundleDealDetails
      parentSkuDetails={parentSkuDetails}
      skuIsVehicleSpecific={skuIsVehicleSpecific}
      deal={deal}
      dealDetails={dealDetailsData}
      constraintSelections={constraintSelections}
      isSaving={isSaving}
      onCartAddClick={addDealToCart}
      onConstraintSwapClick={swapClickHandler}
      onBackClick={onDealDetailsBack}
      onClose={onClose}
      loadError={
        isDealError || isDealConstraintError ? labels.label_deal_bundle_unexpected_error : null
      }
      addToCartError={addToCartError}
    />
  );
};
